/**
 * PhotoUploadSection Component
 * 
 * A unified photo upload component that works with both Events and Bulletins.
 * Handles camera/library selection, photo grid display, and photo management.
 * 
 * Features:
 * - Camera and photo library selection via Alert dialog
 * - 3-photo grid layout with consistent sizing
 * - Photo removal with confirmation
 * - Smart photo limit enforcement
 * - Support for both Event and Bulletin photo types
 * - Automatic photo resizing and optimization
 * - Error handling and user feedback
 * 
 * Photo Types:
 * - 'event': Uses CreateEventPhoto interface (uri property)
 * - 'bulletin': Uses IBulletinPhoto interface (photoUrl, fileUrl properties)
 * 
 * Usage:
 * ```tsx
 * <PhotoUploadSection
 *   title="Event Photos"
 *   subtitle="Add up to 10 photos to showcase your event"
 *   photos={formData.photos}
 *   onPhotosChange={(photos) => setPhotos(photos)}
 *   onAppendPhotos={(newPhotos) => appendPhotos(newPhotos)}
 *   error={errors.photos}
 *   maxPhotos={10}
 *   photoType="event"
 * />
 * ```
 */
import React, { useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  ScrollView,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { Platform } from 'react-native';
import { launchImageLibrary, launchCamera, ImagePickerResponse, MediaType } from 'react-native-image-picker';
import { FormSection } from '@/components';
import { SUPPORTED_IMAGE_MIME_TYPES, SUPPORTED_FORMATS_DISPLAY, isValidImageMimeType } from '@/constants/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { showAlert, showConfirmWithButtons } from '@/utils/common/alert';

const { width: screenWidth } = Dimensions.get('window');
const getPhotoSize = () => {
  // Make thumbnails smaller on desktop web to fit more images per row
  if (Platform.OS === 'web') {
    return 120; // compact thumbnails on desktop
  }
  // Mobile/tablet: keep roughly 3 per row but clamp to a reasonable size
  const approx = (screenWidth - 80) / 3;
  return Math.max(90, Math.min(120, approx));
};
const PHOTO_SIZE = getPhotoSize();

// Generic photo interface that can work with both events and bulletins
export interface GenericPhoto {
  id?: number;           // Make id optional to handle both cases
  uri?: string;          // For events (CreateEventPhoto.uri)
  photoUrl?: string;     // For bulletins (IBulletinPhoto.photoUrl)
  fileUrl?: string;      // For bulletins (IBulletinPhoto.fileUrl)
  type?: string;
  name?: string;
  size?: number;
  file?: any;           // For bulletins
}

interface PhotoUploadSectionProps {
  title: string;
  subtitle: string;
  photos: GenericPhoto[];
  onPhotosChange: (photos: GenericPhoto[]) => void;
  onAppendPhotos?: (photosToAdd: GenericPhoto[]) => void;
  error?: string;
  maxPhotos?: number;
  photoType: 'event' | 'bulletin'; // Determines how to structure the photo object
}

export const PhotoUploadSection: React.FC<PhotoUploadSectionProps> = ({ 
  title,
  subtitle,
  photos,
  onPhotosChange,
  onAppendPhotos,
  error,
  maxPhotos = 10,
  photoType,
}) => {
  const styles = useStyles();
  // handleImageResponse must be defined first as it's used by openCamera and openImageLibrary
  const handleImageResponse = useCallback((response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) {
      return;
    }

    if (response.assets) {
      const baseTimestamp = Date.now();
      
      // Filter and validate file types
      const validAssets = response.assets.filter(asset => {
        if (!asset.type) return true; // Allow if type not specified (fallback to jpeg)
        return isValidImageMimeType(asset.type);
      });
      
      const invalidCount = response.assets.length - validAssets.length;
      if (invalidCount > 0) {
        showAlert(
          'Unsupported File Type',
          `${invalidCount} file(s) were not added. Please select ${SUPPORTED_FORMATS_DISPLAY} images only.`
        );
      }
      
      if (validAssets.length === 0) {
        return;
      }
      
      const newPhotos: GenericPhoto[] = validAssets.map((asset, index) => {
        const basePhoto = {
          id: baseTimestamp + index + Math.random() * 1000,
          type: asset.type || 'image/jpeg',
          name: asset.fileName || `image_${baseTimestamp}_${index}.jpg`,
          size: asset.fileSize,
        };

        if (photoType === 'event') {
          return {
            ...basePhoto,
            uri: asset.uri || '',
          };
        } else {
          // bulletin type
          return {
            ...basePhoto,
            photoUrl: asset.uri || '',
            file: {
              uri: asset.uri || '',
              type: asset.type || 'image/jpeg',
              name: asset.fileName || `image_${baseTimestamp}_${index}.jpg`,
              size: asset.fileSize,
            },
          };
        }
      });

      // Respect maxPhotos by trimming the selection instead of rejecting all
      const availableSlots = Math.max(maxPhotos - photos.length, 0);
      const photosToAdd = newPhotos.slice(0, availableSlots);

      if (photosToAdd.length === 0) {
        showAlert('Too Many Photos', `You can only upload up to ${maxPhotos} photos.`);
        return;
      }

      if (photosToAdd.length < newPhotos.length) {
        showAlert(
          'Photo Limit Reached',
          `Added ${photosToAdd.length} photo(s). ${newPhotos.length - photosToAdd.length} exceeded the limit and were ignored.`
        );
      }

      if (onAppendPhotos) {
        onAppendPhotos(photosToAdd);
      } else {
        onPhotosChange([...photos, ...photosToAdd]);
      }
    }
  }, [photos, onPhotosChange, onAppendPhotos, maxPhotos, photoType]);

  const openCamera = useCallback(() => {
    const options = {
      mediaType: 'photo' as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
    };

    launchCamera(options, handleImageResponse);
  }, [handleImageResponse]);

  const openImageLibrary = useCallback(() => {
    const availableSlots = Math.max(maxPhotos - photos.length, 0);
    // Web: use native input element to match admin-web behavior
    if (Platform.OS === 'web') {
      const input = (globalThis as any).document.createElement('input');
      input.type = 'file';
      input.accept = SUPPORTED_IMAGE_MIME_TYPES.join(',');
      // Allow multiple selection; we'll trim to availableSlots
      input.multiple = availableSlots > 1;
      input.onchange = () => {
        const files = Array.from(input.files || []);
        
        // Filter out unsupported file types
        const validFiles = files.filter((file: any) => isValidImageMimeType(file.type));
        const invalidCount = files.length - validFiles.length;
        
        if (invalidCount > 0) {
          showAlert(
            'Unsupported File Type',
            `${invalidCount} file(s) were not added. Please upload ${SUPPORTED_FORMATS_DISPLAY} images only.`
          );
        }
        
        if (validFiles.length === 0) {
          return;
        }
        
        const trimmed = validFiles.slice(0, availableSlots);
        if (trimmed.length < validFiles.length) {
          showAlert('Too Many Photos', `You can only upload up to ${maxPhotos} photos. ${validFiles.length - trimmed.length} file(s) were not added.`);
        }
        
        const baseTimestamp = Date.now();
        const newPhotos: GenericPhoto[] = trimmed.map((file, index) => ({
          id: baseTimestamp + index + Math.random() * 1000,
          // Use object URL for preview
          photoUrl: URL.createObjectURL(file as any),
          // Attach actual File for upload (handled by convertToFormData on web)
          file: file as any,
          type: (file as any).type || 'image/jpeg',
          name: (file as any).name || `image_${baseTimestamp}_${index}.jpg`,
          size: (file as any).size,
        }));
        if (onAppendPhotos) {
          onAppendPhotos(newPhotos);
        } else {
          onPhotosChange([...photos, ...newPhotos]);
        }
      };
      input.click();
      return;
    }

    // Native platforms
    const options = {
      mediaType: 'photo' as MediaType,
      quality: 0.8 as const,
      maxWidth: 1024,
      maxHeight: 1024,
      // Some platforms only show multi-select UI when selectionLimit is 0 (unlimited)
      // We enforce our limit after selection
      selectionLimit: availableSlots > 1 ? 0 : 1,
    };
    launchImageLibrary(options, handleImageResponse);
  }, [photos, maxPhotos, onAppendPhotos, onPhotosChange, handleImageResponse]);

  const showImagePicker = useCallback(() => {
    // On web, directly open the file picker like admin-web
    if (Platform.OS === 'web') {
      openImageLibrary();
      return;
    }
    showConfirmWithButtons(
      'Select Image',
      'Choose how you want to select an image',
      'Photo Library',
      'Camera',
      () => openImageLibrary(),
      () => openCamera()
    );
  }, [openImageLibrary, openCamera]);

  const removePhoto = useCallback((photoId?: number) => {
    if (photoId === undefined) return;
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    onPhotosChange(updatedPhotos);
  }, [photos, onPhotosChange]);

  const getPhotoUri = (photo: GenericPhoto): string => {
    return photo.uri || photo.photoUrl || photo.fileUrl || '';
  };

  const canAddMore = photos.length < maxPhotos;

  return (
    <FormSection title={title} subtitle={subtitle}>
      <View style={styles.container}>
        <ScrollView
          horizontal={false}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.photosContainer}
        >
          <View style={styles.photosGrid}>
            {photos.map((photo) => (
              <View key={photo.id} style={styles.photoContainer}>
                <Image source={{ uri: getPhotoUri(photo) }} style={styles.photo} />
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => removePhoto(photo.id)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Text style={styles.removeButtonText}>×</Text>
                </TouchableOpacity>
              </View>
            ))}

            {canAddMore && (
              <TouchableOpacity
                style={styles.addPhotoButton}
                onPress={showImagePicker}
                activeOpacity={0.7}
              >
                <Text style={styles.addPhotoIcon}>+</Text>
                <Text style={styles.addPhotoText}>Add Photo</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>

        {error && <Text style={styles.errorText}>{error}</Text>}
      </View>
    </FormSection>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {},
      photosContainer: {
        paddingBottom: 16,
      },
      photosGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        rowGap: 8 as any,
      },
      photoContainer: {
        position: 'relative',
        width: PHOTO_SIZE,
        height: PHOTO_SIZE,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: admin.dataTable.headerBg,
      },
      photo: {
        width: '100%',
        height: '100%',
        resizeMode: 'cover',
      },
      removeButton: {
        position: 'absolute',
        top: 4,
        right: 4,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        alignItems: 'center',
        justifyContent: 'center',
      },
      removeButtonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 16,
      },
      addPhotoButton: {
        width: PHOTO_SIZE,
        height: PHOTO_SIZE,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: admin.dataTable.border,
        borderStyle: 'dashed',
        backgroundColor: admin.dataTable.headerBg,
        alignItems: 'center',
        justifyContent: 'center',
      },
      addPhotoIcon: {
        fontSize: 32,
        color: colors.text.dim,
        marginBottom: 4,
      },
      addPhotoText: {
        fontSize: 12,
        color: colors.text.dim,
        textAlign: 'center',
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 8,
      },
    });
  });
